/**
 * Pure data-shaping for the oggi-vs-tipico region chart.
 *
 * Lives in lib/ (not the component) so it's unit-testable and so the chart
 * component file only exports components (react-refresh / fast-refresh).
 */

export interface TodayPoint {
  hourLocal: number;
  waiting: number | null;
}

/** A typical (historical) point for one local hour. */
export interface TypicalPoint {
  hourLocal: number;
  lo: number | null;
  avg: number | null;
  hi: number | null;
  sampleCount: number;
}

/** A typical point that carries a full, renderable p25–avg–p75 band. */
export interface BandedPoint extends TypicalPoint {
  lo: number;
  avg: number;
  hi: number;
}

/** Fade over ~this many hours at each temporal end of the band. */
const FEATHER_HOURS = 1.5;
/** Never let a feather consume more than this fraction of the span per side. */
const MAX_FEATHER = 0.2;

/**
 * Expand the sparse today array into a contiguous 0–N hour series, filling
 * absent hours with `waiting: null`. A null value instructs LinePath's
 * `defined` guard to break the line, producing an honest gap instead of an
 * interpolated straight segment across missing data.
 */
export function expandToFullDay(sparse: { hourLocal: number; waiting: number }[]): TodayPoint[] {
  const byHour = new Map(sparse.map((p) => [p.hourLocal, p.waiting]));
  const maxHour = sparse.length > 0 ? Math.max(...sparse.map((p) => p.hourLocal)) : -1;
  const result: TodayPoint[] = [];
  for (let h = 0; h <= Math.max(maxHour, 0); h++) {
    result.push({ hourLocal: h, waiting: byHour.get(h) ?? null });
  }
  return result;
}

/** True when this typical point has a full, renderable band (lo, avg, hi all set). */
export function hasBand(p: TypicalPoint): p is BandedPoint {
  return p.lo !== null && p.hi !== null && p.avg !== null;
}

/**
 * A run of *consecutive* hours (hourLocal stepping by 1) that all carry a full
 * band, chosen from the (possibly multiple) well-sampled islands in `typical`.
 *
 * The server nulls lo/avg/hi together for any hour below `@min_samples`, so
 * thin history leaves ragged sparse hours — typically a trailing tail (late
 * evening) but occasionally a leading or interior gap. Drawing the band across
 * those sparse hours makes visx close the Area into an abrupt vertical wedge
 * (the "cliff"). Clipping to a contiguous well-sampled window means the band
 * only exists where we actually have a distribution, and terminates on real
 * data points — the feather (see `bandFeatherStops`) then softens that
 * termination so it reads as "less certain", not a hard fact.
 *
 * When history matures there may be several well-sampled islands (e.g. morning
 * + evening) split by an interior sparse hour. The single-mask feather
 * architecture can only render ONE contiguous band, so we must pick one island
 * and intentionally drop the rest. We prefer the island that contains
 * `preferredHour` (the current hour) — that's where the oggi-vs-tipico
 * comparison actually happens, so it must never be the island we silently drop.
 * We fall back to the longest run only when no island covers "now" (or when no
 * preferred hour is given).
 *
 * Returns an empty array when no banded points exist.
 */
export function contiguousBandWindow(
  typical: readonly TypicalPoint[],
  preferredHour?: number,
): BandedPoint[] {
  const runs: BandedPoint[][] = [];
  let current: BandedPoint[] = [];
  for (const p of typical) {
    if (!hasBand(p)) {
      if (current.length > 0) runs.push(current);
      current = [];
      continue;
    }
    const prev = current[current.length - 1];
    if (prev !== undefined && p.hourLocal !== prev.hourLocal + 1) {
      runs.push(current);
      current = [];
    }
    current.push(p);
  }
  if (current.length > 0) runs.push(current);
  if (runs.length === 0) return [];

  const runStart = (r: BandedPoint[]) => r[0]?.hourLocal ?? Number.POSITIVE_INFINITY;
  const runEnd = (r: BandedPoint[]) => r[r.length - 1]?.hourLocal ?? Number.NEGATIVE_INFINITY;

  if (preferredHour !== undefined) {
    const containing = runs.find((r) => preferredHour >= runStart(r) && preferredHour <= runEnd(r));
    if (containing !== undefined) return containing;
  }
  return runs.reduce<BandedPoint[]>((best, run) => (run.length > best.length ? run : best), []);
}

/**
 * Which temporal ends of the chosen band `window` should be feathered.
 *
 * An end is feathered only when the band was *clipped there because the adjacent
 * hour is sparse* (`!hasBand`) — that's genuine uncertainty. An end that sits at
 * the natural day boundary (00h / 23h) with full sampling is NOT feathered: it's
 * the edge of the axis, not the edge of what we know, so fading it would imply
 * uncertainty that isn't there.
 */
export function bandFeatherEnds(
  typical: readonly TypicalPoint[],
  window: readonly BandedPoint[],
): { left: boolean; right: boolean } {
  if (window.length === 0) return { left: false, right: false };
  const banded = new Set(typical.filter(hasBand).map((p) => p.hourLocal));
  const start = window[0]?.hourLocal ?? 0;
  const end = window[window.length - 1]?.hourLocal ?? 0;
  return {
    left: start > 0 && !banded.has(start - 1),
    right: end < 23 && !banded.has(end + 1),
  };
}

/**
 * Feather fraction (per side) for a band spanning `spanHours` hours: fade over
 * ~`FEATHER_HOURS` at each temporal end, capped at `MAX_FEATHER` so short
 * windows still keep a solid, readable core.
 */
export function bandFeatherFraction(spanHours: number): number {
  if (spanHours <= 0) return 0;
  return Math.min(FEATHER_HOURS / spanHours, MAX_FEATHER);
}

/**
 * Opacity stops for the band's feather gradient/mask, as fractional offsets
 * across the band's x-extent (0 = leading edge, 1 = trailing edge).
 *
 * Each end fades to transparent only when its `featherLeft` / `featherRight`
 * flag is set (a sparsity clip, per `bandFeatherEnds`); an un-feathered end
 * stays fully opaque so a fully-sampled day edge reads as a hard, certain
 * boundary. `feather` is the fraction of the span each fade consumes; it is
 * clamped to [0, 0.49) so the two fades can never overlap and erase the core.
 */
export function bandFeatherStops(
  feather: number,
  featherLeft: boolean,
  featherRight: boolean,
): { offset: number; opacity: number }[] {
  const f = Math.min(Math.max(feather, 0), 0.49);
  return [
    { offset: 0, opacity: featherLeft ? 0 : 1 },
    { offset: f, opacity: 1 },
    { offset: 1 - f, opacity: 1 },
    { offset: 1, opacity: featherRight ? 0 : 1 },
  ];
}
